/**
 * "New design" feature flag for the IronSight redesign.
 *
 * Since 2026-10-16 (docs/decisions/v2-only.md) v2 is the only UI: unless the
 * build sets REACT_APP_ENABLE_V1=true (rollback only, see isV1Enabled), the
 * new design is always on, ?ui=v1 and a stored v1 choice are ignored and
 * nothing is written to storage. The param is still stripped and still counts
 * as a deep link. With the switch on, the original behaviour below applies:
 *
 *   ?ui=v2  → on, remembered in localStorage for the next visits
 *   ?ui=v1  → off again, the remembered choice is forgotten
 *   nothing → whatever was remembered; OFF by default
 *
 * The flag is read once, before the app mounts (see index.js): the `ui` param
 * is taken out of the address so the inventory's URL filters never see it, and
 * the `ui-v2` class goes on <html>. A link that carried the param is still a
 * link to that page, though: arrivedWithUiParam() remembers it for the admin
 * first-load bounce (App.js TenantHome), which reads the query string after
 * the param is gone. Every v2 style is scoped under that
 * class, so with the flag off nothing of the new design can leak into v1, and
 * portals (dialogs, toasts) rendered into <body> are covered too.
 *
 * Storage can be unavailable (private mode, blocked site data); every access
 * is guarded. When it cannot be written, ?ui=v2 still holds for this page load.
 */

import { useState } from 'react';
import { initTheme } from './theme';

export const UI_VERSION_STORAGE_KEY = 'ironsight_ui_version';
export const UI_V2_CLASS = 'ui-v2';
const PARAM = 'ui';
const V2 = 'v2';
const V1 = 'v1';

// Set from the URL when storage could not take it, so the choice survives
// the param being stripped from the address on this page load.
let memoryChoice = null;

// The flag as resolved by initUiVersion() for this page load (QA B10). Once
// set, isUiV2() and useUiV2() return it whatever localStorage says later
// (another tab can write it), so a page can never flip to v1 under the v2
// class on <html>. Null until init (tests that skip init read live).
let resolvedForPageLoad = null;

// Did this page load arrive on a link with ?ui=v1|v2? Set by initUiVersion()
// before it strips the param.
let arrivedWithParam = false;

// Rollback switch, set at build time. Only the exact string 'true' turns v1
// back on: unset, empty, 'false' or an unexpanded pipeline literal like
// $(REACT_APP_ENABLE_V1) all mean off, so a missing setting can never bring
// the old UI back by accident.
export const V1_ENV_FLAG = 'REACT_APP_ENABLE_V1';

/** Can the old UI (v1) be chosen at all? Read per call, like isUiKitAvailable. */
export function isV1Enabled(env = process.env) {
    return env[V1_ENV_FLAG] === 'true';
}

const readStored = () => {
    try { return window.localStorage.getItem(UI_VERSION_STORAGE_KEY); } catch (e) { return null; }
};

const writeStored = (value) => {
    try {
        if (value === V2) window.localStorage.setItem(UI_VERSION_STORAGE_KEY, V2);
        else window.localStorage.removeItem(UI_VERSION_STORAGE_KEY);
        return true;
    } catch (e) {
        return false;
    }
};

const readParam = () => {
    try {
        const value = new URLSearchParams(window.location.search).get(PARAM);
        return value === V2 || value === V1 ? value : null;
    } catch (e) {
        return null;
    }
};

/** Is the new design on for this page load? */
export function isUiV2() {
    if (!isV1Enabled()) return true;
    if (resolvedForPageLoad !== null) return resolvedForPageLoad;
    const param = readParam();
    if (param) return param === V2;
    if (memoryChoice) return memoryChoice === V2;
    return readStored() === V2;
}

const stripParam = () => {
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(PARAM)) return;
        url.searchParams.delete(PARAM);
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (e) { /* leave the address as it is */ }
};

// The v2 mono face is declared in public/index.html but disabled, so that
// v1 keeps rendering its mono stacks exactly as before.
export const V2_FONT_LINK_ID = 'v2-font-mono';

const applyRootClass = (on) => {
    try { document.documentElement.classList.toggle(UI_V2_CLASS, on); } catch (e) { /* no document */ }
    try {
        const font = document.getElementById(V2_FONT_LINK_ID);
        if (font && on) font.disabled = false;
    } catch (e) { /* font stays on its fallback */ }
    // Light / dark (config/theme.js), in the same pass so the first paint is
    // already in the right theme. Off → the attribute is never set.
    initTheme(on);
};

/**
 * Resolve the flag from the URL and storage, persist an explicit choice,
 * clean the address and mark the document root. Call once before mounting.
 */
export function initUiVersion() {
    const param = readParam();
    arrivedWithParam = Boolean(param);
    if (param && isV1Enabled()) {
        memoryChoice = writeStored(param) ? null : param;
    }
    resolvedForPageLoad = null;
    const on = isUiV2();
    resolvedForPageLoad = on;
    stripParam();
    applyRootClass(on);
    return on;
}

/**
 * True when this page load arrived on a link carrying ?ui=v1 or ?ui=v2: the
 * user asked for that page, so it counts as a deep link even after the param
 * has been stripped from the address (QA: a fresh admin tab on /?ui=v2 was
 * bounced to /admin). forgetUiParamArrival() drops it once the visit turns
 * into an ordinary sign-in.
 */
export function arrivedWithUiParam() {
    return arrivedWithParam;
}

export function forgetUiParamArrival() {
    arrivedWithParam = false;
}

/** The flag for components: the page-load value, like the class on <html>. */
export function useUiV2() {
    const [on] = useState(isUiV2);
    return on;
}

// Opt-in for a non-production deployment (set at build time). Never set it
// for production: the kit is a developer page with sample data.
export const UI_KIT_ENV_FLAG = 'REACT_APP_ENABLE_UI_KIT';

/**
 * The living UI kit at /ui-kit: only in local development (`npm start`) or
 * when the build sets REACT_APP_ENABLE_UI_KIT=true. A production build
 * without that flag never has the route, whatever the "new design" flag says.
 */
export function isUiKitAvailable(env = process.env) {
    return env.NODE_ENV === 'development' || env[UI_KIT_ENV_FLAG] === 'true';
}

/** Tests only: forget the in-memory fallback. */
export function __resetUiVersionForTests() {
    memoryChoice = null;
    resolvedForPageLoad = null;
    arrivedWithParam = false;
}
